import React from 'react';
import { cn } from '@/lib/cn';
import { SectionHeader } from '@/components/ui';
import { CheckboxIcon } from '@/components/ui';
import { useStore } from '@/providers/StoreProvider';
import { COMPLIANCES_CONFIG, type ComplianceConfig } from './filterConfigs';
import type { CompliancesState } from '@repo/store';
import ListCell from '@/components/ui/ListCell';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface CompliancesFilterProps {
  className?: string;
}

export function useCompliancesFilter() {
  const analytics = useAnalytics();
  const compliances = useStore(s => s.listingsFilters.compliances);
  const toggleComplianceFilterRaw = useStore(s => s.listingsFilters.toggleComplianceFilter);
  const resetComplianceFilter = useStore(s => s.listingsFilters.resetComplianceFilter);
  const resetAllCompliances = useStore(s => s.listingsFilters.resetAllCompliances);

  const toggleComplianceFilter = (key: keyof CompliancesState) => {
    const eventName =
      key === 'hasNoBuildingOneYearBedbugHistory'
        ? 'listings_filter_all_no_bedbug'
        : key === 'hasNoBuildingEvictionHistory'
          ? 'listings_filter_all_no_evictions'
          : key === 'hasNoBuildingPendingLitigation'
            ? 'listings_filter_all_no_litigation'
            : 'listings_filter_all_no_violations';

    const nextVal = compliances[key] === true ? null : true;
    analytics.track(eventName, { val: nextVal });
    toggleComplianceFilterRaw(key);
  };

  const isComplianceSelected = (key: keyof CompliancesState) => {
    return compliances[key] === true;
  };

  const getComplianceValue = (key: keyof CompliancesState) => {
    return compliances[key];
  };

  return {
    compliances,
    toggleComplianceFilter,
    resetComplianceFilter,
    resetAllCompliances,
    isComplianceSelected,
    getComplianceValue,
  };
}

export function CompliancesFilter({ className }: CompliancesFilterProps) {
  const { compliances, toggleComplianceFilter } = useCompliancesFilter();

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {COMPLIANCES_CONFIG.map(({ key, label }) => {
        const selected = compliances[key] === true;
        return (
          <ListCell
            key={key}
            title={label}
            trailing={<CheckboxIcon checked={selected} />}
            onClick={() => toggleComplianceFilter(key)}
            className="cursor-pointer"
          />
        );
      })}
    </div>
  );
}

export default CompliancesFilter;
